// Utility functions to transform API data for UI components

// Helper function to get validation with lowest relationship value for each requestorItemId
export const getValidationWithLowestRelationship = (validationArray) => {
  if (!Array.isArray(validationArray) || validationArray.length === 0) {
    return [];
  }

  // Group validations by requestorItemId
  const groupedByRequestorItem = validationArray.reduce((acc, validation) => {
    const requestorItemId = validation.requestorItemId;
    if (!acc[requestorItemId]) {
      acc[requestorItemId] = [];
    }
    acc[requestorItemId].push(validation);
    return acc;
  }, {});

  // For each group, find the validation with the lowest relationship value
  const filteredValidations = Object.values(groupedByRequestorItem).map(group => {
    return group.reduce((lowest, current) => {
      const currentRelationship = current.relationship || '';
      const lowestRelationship = lowest.relationship || '';
      
      // Extract the number from relationship (e.g., "V1" -> 1, "V2" -> 2)
      const currentNumber = parseInt(currentRelationship.replace('V', '')) || 999;
      const lowestNumber = parseInt(lowestRelationship.replace('V', '')) || 999;
      
      return currentNumber < lowestNumber ? current : lowest;
    });
  });

  return filteredValidations;
};

export const transformSkillData = (externalData) => {
  if (!externalData) return null;
  
  const { skills: apiSkills, skillsApplied, skillsAcquired, skillings, employments, worksProjects, validation } = externalData;
  
  // Filter validation data to show only the lowest relationship value for each requestorItemId
  const filteredValidation = getValidationWithLowestRelationship(validation || []);
  
  // Transform skills from the new API structure
  let skills = [];
  if (Array.isArray(apiSkills) && apiSkills.length > 0) {
    skills = apiSkills
      .filter(s => s.status !== "marked for deletion") // Filter out deleted skills
      .map((s) => {
        const appliedDays = parseInt(s.skillAppliedExp, 10) || 0; // API gives days
        const acquiredDays = parseInt(s.skillAcquiredExp, 10) || 0; // API gives days
        const appliedYMD = daysToYMD(appliedDays);
        const acquiredYMD = daysToYMD(acquiredDays);
        return {
          id: s.id || s.skillId,
          name: s.skill || s.skillOccupation || "",
          category: s.skillCategory,
          occupation: s.occupation,
          userRank: s.userRank,
          isCurrentApplied: (s.isCurrentApplied || s.isCurrent) === "Yes",
          appliedDays,
          acquiredDays,
          experience: { years: appliedYMD.years, months: appliedYMD.months, days: appliedYMD.days, totalDays: appliedDays },
          acquiredExperience: { years: acquiredYMD.years, months: acquiredYMD.months, days: acquiredYMD.days, totalDays: acquiredDays }
        };
      });
  } else {
    // Fallback to legacy skillings if new structure not available
    skills = skillings?.map(skill => {
      const exp = calculateExperience(skill.startDate, skill.endDate, skill.isCurrent);
      const appliedDays = exp.totalDays || 0;
      const appliedYMD = daysToYMD(appliedDays);
      const acquiredDays = 0;
      const acquiredYMD = daysToYMD(acquiredDays);
      return {
        id: skill.id,
        name: skill.skillingName || skill.title,
        startDate: skill.startDate,
        endDate: skill.endDate,
        duration: skill.duration,
        organization: skill.organization,
        isCurrent: skill.isCurrent === "Yes",
        experience: { years: appliedYMD.years, months: appliedYMD.months, days: appliedYMD.days, totalDays: appliedDays },
        appliedDays,
        acquiredDays,
        acquiredExperience: { years: acquiredYMD.years, months: acquiredYMD.months, days: acquiredYMD.days, totalDays: acquiredDays }
      };
    }) || [];
  }

  const sortedSkills = [...skills].sort((a, b) => {
    const aRank = a.userRank || 0;
    const bRank = b.userRank || 0;
    if (aRank !== bRank) return aRank - bRank; // Lower rank first
    const aDays = typeof a.appliedDays === 'number' ? a.appliedDays : (a.duration || 0);
    const bDays = typeof b.appliedDays === 'number' ? b.appliedDays : (b.duration || 0);
    return bDays - aDays;
  });
  
  const top5Skills = sortedSkills.slice(0, 5);
  const otherSkills = sortedSkills.slice(5);

  const appliedApplications = getAppliedSkillApplications(sortedSkills, skillsApplied, employments, worksProjects, filteredValidation);
  const acquiredApplications = getAcquiredSkillApplications(sortedSkills, skillsAcquired, employments, worksProjects, filteredValidation);
  
  // Debug logging removed for production

  return {
    top5Skills: top5Skills.map(s => s.name),
    otherSkills: otherSkills.map(s => s.name),
    skillDetails: sortedSkills,
    applications: getSkillApplicationsFromNewStructure(sortedSkills, skillsApplied, skillsAcquired, employments, worksProjects, filteredValidation),
    appliedApplications,
    acquiredApplications
  };
};

const calculateExperience = (startDate, endDate, isCurrent) => {
  const start = new Date(parseInt(startDate));
  const end = isCurrent === "Yes" ? new Date() : new Date(parseInt(endDate));
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);
  
  return { years, months, totalDays: diffDays };
};

// Convert total days to precise years, months, days without approximation
export function daysToYMD(totalDays) {
  const safeDays = Number.isFinite(totalDays) ? Math.max(0, Math.floor(totalDays)) : 0;
  const years = Math.floor(safeDays / 365);
  const daysAfterYears = safeDays % 365;
  const months = Math.floor(daysAfterYears / 30);
  const days = daysAfterYears % 30;
  return { years, months, days };
}

const getAppliedSkillApplications = (skills, skillsApplied, employments, worksProjects, validation = []) => {
  if (!skills) return [];
  
  return skills.map(skill => {
    const appliedEntries = skillsApplied?.filter(app => 
      app.userSkillsId === skill.id && app.status !== "marked for deletion"
    ) || [];
    
    
    const applications = appliedEntries.map(app => {
      // Find validation data for this application
      const validationData = validation?.filter(v => v.requestorItemId === app.id) || [];
      
      return {
        id: app.id,
        title: app.projectActivity || app.title || app.organization || '',
        location: app.location,
        fromDate: app.fromDate,
        toDate: app.toDate,
        isCurrent: app.isCurrent,
        duration: formatDuration(app.fromDate, app.toDate, app.isCurrent),
        description: app.briefDescriptions || '',
        organization: app.organization || '',
        attachmentExists: app.attachmentExists || false,
        validationData: validationData
      };
    });
    
    return {
      skillName: skill.name,
      applications: applications
    };
  });
};

const getAcquiredSkillApplications = (skills, skillsAcquired, employments, worksProjects, validation = []) => {
  if (!skills) return [];
  
  return skills.map(skill => {
    const acquiredEntries = skillsAcquired?.filter(acq => 
      acq.userSkillsId === skill.id && acq.status !== "marked for deletion"
    ) || [];
    
    
    const applications = acquiredEntries.map(acq => {
      // Find validation data for this acquisition
      const validationData = validation?.filter(v => v.requestorItemId === acq.id) || [];
      
      return {
        id: acq.id,
        title: acq.source || acq.title || acq.organization || '',
        location: acq.location,
        fromDate: acq.fromDate,
        toDate: acq.toDate,
        isCurrent: acq.isCurrent,
        duration: formatDuration(acq.fromDate, acq.toDate, acq.isCurrent),
        description: acq.briefDescriptions || '',
        organization: acq.organization || '',
        attachmentExists: acq.attachmentExists || false,
        validationData: validationData
      };
    });
    
    return {
      skillName: skill.name,
      applications: applications
    };
  });
};

const getSkillApplicationsFromNewStructure = (skills, skillsApplied, skillsAcquired, employments, worksProjects, validation = []) => {
  if (!skills) return [];
  
  return skills.map(skill => {
    const appliedEntries = skillsApplied?.filter(app => 
      app.userSkillsId === skill.id && app.status !== "marked for deletion"
    ) || [];
    
    const acquiredEntries = skillsAcquired?.filter(acq => 
      acq.userSkillsId === skill.id && acq.status !== "marked for deletion"
    ) || [];
    
    const applications = [
      ...appliedEntries.map(app => {
        // Find validation data for this application
        const validationData = validation?.filter(v => v.requestorItemId === app.id) || [];
        
        return {
          id: app.id,
          title: app.projectActivity || app.title || app.organization || '',
          location: app.location,
          fromDate: app.fromDate,
          toDate: app.toDate,
          isCurrent: app.isCurrent,
          duration: formatDuration(app.fromDate, app.toDate, app.isCurrent),
          description: app.briefDescriptions || '',
          organization: app.organization || '',
          attachmentExists: app.attachmentExists || false,
          validationData: validationData
        };
      }),
      ...acquiredEntries.map(acq => {
        // Find validation data for this acquisition
        const validationData = validation?.filter(v => v.requestorItemId === acq.id) || [];
        
        return {
          id: acq.id,
          title: acq.source || acq.title || acq.organization || '',
          location: acq.location,
          fromDate: acq.fromDate,
          toDate: acq.toDate,
          isCurrent: acq.isCurrent,
          duration: formatDuration(acq.fromDate, acq.toDate, acq.isCurrent),
          description: acq.briefDescriptions || '',
          organization: acq.organization || '',
          attachmentExists: acq.attachmentExists || false,
          validationData: validationData
        };
      })
    ];
    
    return {
      skillName: skill.name,
      applications: applications
    };
  });
};


const formatDuration = (fromDate, toDate, isCurrent) => {
  const start = new Date(parseInt(fromDate));
  const end = isCurrent === "Yes" ? new Date() : new Date(parseInt(toDate));
  
  const startMonth = start.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const endMonth = isCurrent === "Yes" ? 'Present' : end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  
  // More accurate calculation using date methods
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  
  // Adjust for day difference
  if (end.getDate() < start.getDate()) {
    months--;
  }
  
  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }
  
  let durationText = '';
  
  // Handle very short durations (less than 1 month)
  if (years === 0 && months === 0) {
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 30) {
      return `${startMonth} - ${endMonth}`; // No parentheses for very short durations
    }
  }
  
  // Build duration text
  if (years > 0) {
    durationText += `${years} Year${years > 1 ? 's' : ''}`;
  }
  if (months > 0) {
    if (durationText) durationText += ' ';
    durationText += `${months} Month${months > 1 ? 's' : ''}`;
  }
  
  // Return with parentheses only if we have duration text
  return durationText ? 
    `${startMonth} - ${endMonth} (${durationText})` : 
    `${startMonth} - ${endMonth}`;
};

// Helper function to format experience for Skills tab
export const formatExperience = (years, months) => {
  if (years === 0 && months === 0) {
    return '0 Days';
  }
  let experienceText = '';
  if (years > 0) {
    experienceText += `${years} Year${years > 1 ? 's' : ''}`;
  }
  if (months > 0) {
    if (experienceText) experienceText += ' ';
    experienceText += `${months} Month${months > 1 ? 's' : ''}`;
  }
  return experienceText || '0 Days';
};

// Format total days into string per rule:
// - If years > 0: show years and months
// - Else if months > 0: show months and days
// - Else: show days
export const formatExperienceFromDays = (totalDays) => {
  const { years, months, days } = daysToYMD(totalDays);
  if (years > 0) {
    return `${years} Year${years > 1 ? 's' : ''}${months > 0 ? ` ${months} Month${months > 1 ? 's' : ''}` : ''}`;
  }
  if (months > 0) {
    return `${months} Month${months > 1 ? 's' : ''}${days > 0 ? ` ${days} Day${days > 1 ? 's' : ''}` : ''}`;
  }
  return `${days} Day${days === 1 ? '' : 's'}`;
};

// Transform employment data for UI
export const transformEmploymentData = (externalData) => {
  if (!externalData) return null;
  
  const { employments, worksProjects, validation } = externalData;
  
  // Filter validation data to show only the lowest relationship value for each requestorItemId
  const filteredValidation = getValidationWithLowestRelationship(validation || []);
  
  // Group employments by organization
  const groupedEmployments = employments?.reduce((acc, emp) => {
    if (!acc[emp.organization]) {
      acc[emp.organization] = {
        id: emp.id,
        organization: emp.organization,
        designation: emp.designation,
        empType: emp.empType,
        isCurrent: emp.isCurrent === "Yes",
        fromDate: emp.fromDate,
        toDate: emp.toDate,
        duration: formatDuration(emp.fromDate, emp.toDate, emp.isCurrent),
        description: emp.briefDescriptions,
        kpi: emp.kpi,
        location: emp.location,
        projects: []
      };
    } else {
      // For subsequent employments with the same organization, update description and KPI
      // if the current employment has them and the existing one doesn't, or if current is more recent
      if (emp.briefDescriptions && (!acc[emp.organization].description || emp.fromDate > acc[emp.organization].fromDate)) {
        acc[emp.organization].description = emp.briefDescriptions;
      }
      if (emp.kpi && (!acc[emp.organization].kpi || emp.fromDate > acc[emp.organization].fromDate)) {
        acc[emp.organization].kpi = emp.kpi;
      }
    }
    
    // Get validation data for this employment
    const employmentValidationData = filteredValidation?.filter(v => v.requestorItemId === emp.id) || [];
    
    // Add validation data to the employment object if not already present
    if (employmentValidationData.length > 0 && !acc[emp.organization].validationData) {
      acc[emp.organization].validationData = employmentValidationData;
    }
    
    // Get related projects for this employment using only connectedId
    const relatedProjects = worksProjects?.filter(project => 
      project.connectedId === emp.id
    ) || [];
    
    // Add related projects only (not the employment itself)
    relatedProjects.forEach(project => {
      const projectValidationData = filteredValidation?.filter(v => v.requestorItemId === project.id) || [];
      acc[emp.organization].projects.push({
        id: project.id,
        title: project.projectActivity || "",
        role: project.designation || "",
        location: project.location || emp.location || "",
        fromDate: project.fromDate,
        toDate: project.toDate,
        isCurrent: project.isCurrent,
        duration: formatDuration(project.fromDate, project.toDate, project.isCurrent),
        description: project.briefDescriptions,
        kpi: project.kpi,
        attachmentExists: project.attachmentExists || false,
        validationData: projectValidationData
      });
    });
    
    return acc;
  }, {}) || {};

  return {
    employments: Object.values(groupedEmployments),
    allEmployments: employments || []
  };
};



// Transform learnings data for UI
export const transformLearningsData = (externalData) => {
  if (!externalData) return null;
  
  const { education, certifications, trainings, validation } = externalData;
  
  // Filter validation data to show only the lowest relationship value for each requestorItemId
  const filteredValidation = getValidationWithLowestRelationship(validation || []);
  
  // Transform education data
  const transformedEducation = education?.map(edu => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === edu.id) || [];
    return {
      id: edu.id,
      title: edu.course,
      institute: edu.institute,
      startDate: edu.startDate,
      endDate: edu.endDate,
      duration: formatDuration(edu.startDate, edu.endDate, edu.isCurrent),
      isCurrent: edu.isCurrent === "Yes",
      description: edu.briefDescriptions,
      cgpa: edu.cgpa,
      kpi: edu.kpi,
      type: "Education",
      attachmentExists: edu.attachmentExists || false,
      validationData: validationData
    };
  }) || [];

  // Transform certifications data
  const transformedCertifications = certifications?.map(cert => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === cert.id) || [];
    return {
      id: cert.id,
      title: cert.certificationName,
      organization: cert.organization,
      startDate: cert.startDate,
      endDate: cert.endDate,
      duration: formatDuration(cert.startDate, cert.endDate, cert.isCurrent),
      isCurrent: cert.isCurrent === "Yes",
      description: cert.briefDescription,
      type: "Certification",
      attachmentExists: cert.attachmentExists || false,
      validationData: validationData
    };
  }) || [];

  // Transform trainings data
  const transformedTrainings = trainings?.map(training => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === training.id) || [];
    return {
      id: training.id,
      title: training.trainingName || training.title,
      organization: training.organization,
      startDate: training.startDate,
      endDate: training.endDate,
      duration: formatDuration(training.startDate, training.endDate, training.isCurrent),
      isCurrent: training.isCurrent === "Yes",
      description: training.briefDescriptions,
      type: "Training",
      attachmentExists: training.attachmentExists || false,
      validationData: validationData
    };
  }) || [];

  // Combine all learnings data
  const allLearnings = [
    ...transformedEducation,
    ...transformedCertifications,
    ...transformedTrainings
  ];

  return {
    education: transformedEducation,
    certifications: transformedCertifications,
    trainings: transformedTrainings,
    allLearnings: allLearnings
  };
};

// Transform others data for UI (awards, licenses, achievements, etc.)
export const transformOthersData = (externalData) => {
  if (!externalData) return null;

  const { award, memberships, languages, licenses, achievements, other, validation } = externalData;
  
  // Filter validation data to show only the lowest relationship value for each requestorItemId
  const filteredValidation = getValidationWithLowestRelationship(validation || []);

  // Transform awards data
  const transformedAwards = award?.map(awardItem => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === awardItem.id) || [];  
    return {
      id: awardItem.id,
      title: awardItem.awardName || awardItem.title,
      organization: awardItem.organization,
      date: awardItem.date,
      description: awardItem.briefDescriptions,
      type: "Award",
      attachmentExists: awardItem.attachmentExists || false,
      validationData: validationData
    };
  }) || [];

  // Transform memberships data
  const transformedMemberships = (memberships || []).map((m) => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === m.id) || [];
    return {
      id: m.id,
      title: m.title || m.organization || 'Member',
      organization: m.organization || '',
      startDate: m.startDate || m.fromDate || m.createdTime || null,
      endDate: m.endDate || m.toDate || (m.isCurrent === 'Yes' ? 'Present' : null),
      duration: formatDuration(m.startDate || m.fromDate || m.createdTime, m.endDate || m.toDate, m.isCurrent),
      description: m.briefDescriptions || '',
      type: 'Membership',
      attachmentExists: m.attachmentExists || false,
      validationData
    };
  });

  // Transform languages data
  const transformedLanguages = (languages || []).map((l) => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === l.id) || [];
    
    // Build simple skills text without "Skills:" prefix
    const skills = [];
    if (l.mystRead === 'Yes') skills.push('Read');
    if (l.mystWrite === 'Yes') skills.push('Write');
    if (l.mystSpeak === 'Yes') skills.push('Speak');
    
    const skillsText = skills.length > 0 ? skills.join(' ') : '';
    
    return {
      id: l.id,
      title: l.userLanguage || '',
      skills: {
        speak: l.mystSpeak === 'Yes',
        write: l.mystWrite === 'Yes',
        read: l.mystRead === 'Yes'
      },
      skillsText: skillsText,
      type: 'Language',
      attachmentExists: l.attachmentExists || false,
      validationData
    };
  });

  // Transform licenses data
  const transformedLicenses = (licenses || []).map((lic) => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === lic.id) || [];
    return {
      id: lic.id,
      title: lic.title || lic.licenseName || '',
      organization: lic.organization || '',
      startDate: lic.startDate || lic.fromDate || lic.createdTime || null,
      endDate: lic.endDate || lic.toDate || (lic.isCurrent === 'Yes' ? 'Present' : null),
      duration: formatDuration(lic.startDate || lic.fromDate || lic.createdTime, lic.endDate || lic.toDate, lic.isCurrent),
      description: lic.briefDescriptions || '',
      type: 'License',
      attachmentExists: lic.attachmentExists || false,
      validationData
    };
  });

  // Transform achievements data
  const transformedAchievements = (achievements || []).map((a) => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === a.id) || [];
    return {
      id: a.id,
      title: a.title || a.achievementName || '',
      organization: a.organization || '',
      startDate: a.startDate || a.fromDate || a.createdTime || null,
      endDate: a.endDate || a.toDate || (a.isCurrent === 'Yes' ? 'Present' : null),
      duration: formatDuration(a.startDate || a.fromDate || a.createdTime, a.endDate || a.toDate, a.isCurrent),
      description: a.briefDescriptions || '',
      type: 'Achievement',
      attachmentExists: a.attachmentExists || false,
      validationData
    };
  });

  const transformedOther = (other || []).map((o) => {
    const validationData = filteredValidation?.filter(v => v.requestorItemId === o.id) || [];
    return {
      id: o.id,
      title: o.title || 'Other',
      organization: o.organization || '',
      startDate: o.startDate || o.fromDate || o.createdTime || null,
      endDate: o.endDate || o.toDate || (o.isCurrent === 'Yes' ? 'Present' : null),
      duration: formatDuration(o.startDate || o.fromDate || o.createdTime, o.endDate || o.toDate, o.isCurrent),
      description: o.briefDescriptions || o.description || '',
      type: 'Other',
      attachmentExists: o.attachmentExists || false,
      validationData
    };
  });

  // Combine all others data
  const allOthers = [
    ...transformedAwards,
    ...transformedLicenses,
    ...transformedAchievements,
    ...transformedMemberships,
    ...transformedLanguages,
    ...transformedOther
  ];

  return {
    awards: transformedAwards,
    licenses: transformedLicenses,
    achievements: transformedAchievements,
    memberships: transformedMemberships,
    languages: transformedLanguages,
    other: transformedOther,
    allOthers: allOthers
  };
};

// Transform user profile data from API
export const transformUserProfileData = (apiData) => {
  if (!apiData) return null;

  const { externalData } = apiData;

  return {
    skillData: transformSkillData(externalData),
    employmentData: transformEmploymentData(externalData),
    learningsData: transformLearningsData(externalData),
    othersData: transformOthersData(externalData),
    educationData: externalData?.education || [],
    certificationData: externalData?.certifications || [],
    trainingData: externalData?.trainings || [],
    projectData: externalData?.worksProjects || [],
    awardData: externalData?.award || []
  };
};
